import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Search, Plus, Trash2, ArrowLeft, Download, Upload, Copy, Code } from 'lucide-react';
import {
  apiService,
  SpeechToTextProvider,
  SttModel,
  SttModelConfigSchema,
  LocalizedText
} from '../../services/api';
import { copyToClipboard } from '../../utils/clipboard';

function getLocalizedText(text: LocalizedText | undefined, lang = 'de'): string {
  if (!text) return '';
  if (typeof text === 'string') return text;
  return text[lang] || text['en'] || Object.values(text)[0] || '';
}

interface ModelFormData {
  name: string;
  displayNameDe: string;
  displayNameEn: string;
  descriptionDe: string;
  descriptionEn: string;
  ranking: string;
  configSchemaJson: string;
}

const emptyFormData: ModelFormData = {
  name: '',
  displayNameDe: '',
  displayNameEn: '',
  descriptionDe: '',
  descriptionEn: '',
  ranking: '',
  configSchemaJson: '{}'
};

function formDataToModel(form: ModelFormData): SttModel {
  const displayName: Record<string, string> = {};
  if (form.displayNameDe) displayName['de'] = form.displayNameDe;
  if (form.displayNameEn) displayName['en'] = form.displayNameEn;

  const description: Record<string, string> = {};
  if (form.descriptionDe) description['de'] = form.descriptionDe;
  if (form.descriptionEn) description['en'] = form.descriptionEn;

  let configSchema: SttModelConfigSchema | undefined;
  try {
    const parsed = JSON.parse(form.configSchemaJson);
    if (Object.keys(parsed).length > 0) {
      configSchema = parsed as SttModelConfigSchema;
    }
  } catch {
    // keep undefined
  }

  const model: SttModel = {
    name: form.name,
    displayName: Object.keys(displayName).length > 0 ? displayName : form.name,
    ...(Object.keys(description).length > 0 && { description }),
    ...(form.ranking !== '' && { ranking: Number(form.ranking) }),
    ...(configSchema && { configSchema })
  };
  return model;
}

export function SttModelsPage() {
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();

  const [provider, setProvider] = useState<SpeechToTextProvider | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  // Create/Edit dialog
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<ModelFormData>(emptyFormData);

  // Export dialog
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportJson, setExportJson] = useState('');

  // Import dialog
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importPreview, setImportPreview] = useState<SttModel[] | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadProvider = useCallback(async () => {
    if (!providerId) return;
    try {
      setLoading(true);
      const data = await apiService.getSpeechToTextProvider(providerId);
      setProvider(data);
      setError(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler beim Laden des Providers';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    loadProvider();
  }, [loadProvider]);

  const saveModels = async (models: SttModel[]) => {
    if (!provider) return;
    try {
      setSaving(true);
      await apiService.updateSpeechToTextProvider(provider.id, { models });
      setProvider({ ...provider, models });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler beim Speichern';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  const filteredModels = provider?.models.filter(model => {
    const term = searchTerm.toLowerCase();
    const name = model.name.toLowerCase();
    const displayName = getLocalizedText(model.displayName).toLowerCase();
    const description = getLocalizedText(model.description).toLowerCase();
    return name.includes(term) || displayName.includes(term) || description.includes(term);
  }) ?? [];

  // Create / Edit handlers
  const openCreateDialog = () => {
    setEditingIndex(null);
    setFormData(emptyFormData);
    setIsEditDialogOpen(true);
  };

  const handleSaveModel = async () => {
    if (!provider) return;
    if (!formData.name.trim()) {
      alert('Modellname ist erforderlich');
      return;
    }

    let configSchemaValid = true;
    try {
      JSON.parse(formData.configSchemaJson);
    } catch {
      configSchemaValid = false;
    }
    if (!configSchemaValid) {
      alert('Ungültiges JSON im Config Schema');
      return;
    }

    const model = formDataToModel(formData);
    const newModels = [...provider.models];

    if (editingIndex !== null) {
      newModels[editingIndex] = model;
    } else {
      // Check for duplicate name
      if (newModels.some(m => m.name === model.name)) {
        alert(`Ein Modell mit dem Namen "${model.name}" existiert bereits`);
        return;
      }
      newModels.push(model);
    }

    await saveModels(newModels);
    setIsEditDialogOpen(false);

    // Navigate to detail page after creating a new model
    if (editingIndex === null) {
      navigate(`/speech-to-text/${providerId}/models/${encodeURIComponent(model.name)}`);
    }
  };

  const handleDeleteModel = async (filteredIndex: number) => {
    if (!provider) return;
    const model = filteredModels[filteredIndex];
    if (!confirm(`Modell "${getLocalizedText(model.displayName)}" wirklich löschen?`)) return;
    const newModels = provider.models.filter(m => m !== model);
    await saveModels(newModels);
  };

  // Export
  const openExportDialog = () => {
    if (!provider) return;
    setExportJson(JSON.stringify(provider.models, null, 2));
    setIsExportDialogOpen(true);
  };

  const handleCopyExport = async () => {
    const copied = await copyToClipboard(exportJson, 'Export-JSON');
    if (!copied) {
      // Fallback: select all text
      const textarea = document.querySelector<HTMLTextAreaElement>('#export-json');
      textarea?.select();
    }
  };

  const handleDownloadExport = () => {
    if (!provider) return;
    const blob = new Blob([exportJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${provider.name}-models.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Import
  const openImportDialog = () => {
    setImportJson('');
    setImportPreview(null);
    setImportError(null);
    setIsImportDialogOpen(true);
  };

  const validateImportJson = (json: string) => {
    setImportJson(json);
    setImportError(null);
    setImportPreview(null);
    if (!json.trim()) return;

    try {
      const parsed = JSON.parse(json);
      if (!Array.isArray(parsed)) {
        setImportError('JSON muss ein Array von Modellen sein');
        return;
      }
      for (let i = 0; i < parsed.length; i++) {
        const m = parsed[i];
        if (!m.name || typeof m.name !== 'string') {
          setImportError(`Modell an Index ${i}: "name" fehlt oder ist ungültig`);
          return;
        }
        if (!m.displayName) {
          setImportError(`Modell an Index ${i}: "displayName" fehlt`);
          return;
        }
      }
      setImportPreview(parsed as SttModel[]);
    } catch (e) {
      setImportError(`Ungültiges JSON: ${e instanceof Error ? e.message : 'Parsefehler'}`);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const content = ev.target?.result;
      if (typeof content === 'string') {
        validateImportJson(content);
      }
    };
    reader.readAsText(file);
    // Reset file input so the same file can be selected again
    e.target.value = '';
  };

  const handleImportConfirm = async () => {
    if (!provider || !importPreview) return;
    // Merge: replace models with same name, add new ones
    const mergedModels = [...provider.models];
    for (const importModel of importPreview) {
      const existingIdx = mergedModels.findIndex(m => m.name === importModel.name);
      if (existingIdx >= 0) {
        mergedModels[existingIdx] = importModel;
      } else {
        mergedModels.push(importModel);
      }
    }
    await saveModels(mergedModels);
    setIsImportDialogOpen(false);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12 text-gray-500">Lade Modelle...</div>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="text-red-500 mb-4">{error || 'Provider nicht gefunden'}</div>
          <Button onClick={() => navigate('/speech-to-text')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/speech-to-text')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Modelle: {provider.displayName}
            </h1>
            <p className="text-gray-600">
              {provider.models.length} Modell{provider.models.length !== 1 ? 'e' : ''} konfiguriert
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={openExportDialog}>
            <Download className="w-4 h-4 mr-2" />
            Exportieren
          </Button>
          <Button variant="outline" size="sm" onClick={openImportDialog}>
            <Upload className="w-4 h-4 mr-2" />
            Importieren
          </Button>
          <Button onClick={openCreateDialog} disabled={saving}>
            <Plus className="w-4 h-4 mr-2" />
            Neues Modell
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Modell suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Model Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredModels.map((model, index) => (
          <Card
            key={model.name}
            className="hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => navigate(`/speech-to-text/${providerId}/models/${encodeURIComponent(model.name)}`)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-lg truncate">
                    {getLocalizedText(model.displayName)}
                  </CardTitle>
                  <code className="text-xs text-gray-500">{model.name}</code>
                </div>
                {model.ranking !== undefined && (
                  <Badge variant="secondary" className="ml-2 shrink-0">
                    #{model.ranking}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {model.description && (
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {getLocalizedText(model.description)}
                  </p>
                )}

                {model.configSchema && Object.keys(model.configSchema).length > 0 && (
                  <div>
                    <div className="flex items-center space-x-1 mb-1">
                      <Code className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-500">
                        {Object.keys(model.configSchema).length} Konfigurationsfelder
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {Object.keys(model.configSchema).slice(0, 4).map(key => (
                        <Badge key={key} variant="outline" className="text-xs">
                          {key}
                        </Badge>
                      ))}
                      {Object.keys(model.configSchema).length > 4 && (
                        <Badge variant="outline" className="text-xs">
                          +{Object.keys(model.configSchema).length - 4}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-2 pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleDeleteModel(index); }}
                    className="text-red-600 hover:text-red-700"
                    disabled={saving}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredModels.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">
              {searchTerm ? 'Keine Modelle gefunden.' : 'Noch keine Modelle konfiguriert.'}
            </p>
            {!searchTerm && (
              <div className="mt-4 space-x-2">
                <Button onClick={openCreateDialog}>Erstes Modell erstellen</Button>
                <Button variant="outline" onClick={openImportDialog}>
                  <Upload className="w-4 h-4 mr-2" />
                  Importieren
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingIndex !== null ? 'Modell bearbeiten' : 'Neues Modell erstellen'}
            </DialogTitle>
            <DialogDescription>
              Konfigurieren Sie die Modelleigenschaften und das Config Schema
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="model-name">Name (technisch)</Label>
                <Input
                  id="model-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. nova-3"
                  disabled={editingIndex !== null}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model-ranking">Ranking</Label>
                <Input
                  id="model-ranking"
                  type="number"
                  value={formData.ranking}
                  onChange={(e) => setFormData({ ...formData, ranking: e.target.value })}
                  placeholder="z.B. 1 (niedriger = höhere Priorität)"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="model-displayname-de">Anzeigename (Deutsch)</Label>
                <Input
                  id="model-displayname-de"
                  value={formData.displayNameDe}
                  onChange={(e) => setFormData({ ...formData, displayNameDe: e.target.value })}
                  placeholder="z.B. Nova 3"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model-displayname-en">Anzeigename (English)</Label>
                <Input
                  id="model-displayname-en"
                  value={formData.displayNameEn}
                  onChange={(e) => setFormData({ ...formData, displayNameEn: e.target.value })}
                  placeholder="e.g. Nova 3"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="model-description-de">Beschreibung (Deutsch)</Label>
                <Input
                  id="model-description-de"
                  value={formData.descriptionDe}
                  onChange={(e) => setFormData({ ...formData, descriptionDe: e.target.value })}
                  placeholder="Kurze Beschreibung auf Deutsch"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model-description-en">Beschreibung (English)</Label>
                <Input
                  id="model-description-en"
                  value={formData.descriptionEn}
                  onChange={(e) => setFormData({ ...formData, descriptionEn: e.target.value })}
                  placeholder="Short description in English"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="model-configschema">Config Schema (JSON)</Label>
              <Textarea
                id="model-configschema"
                value={formData.configSchemaJson}
                onChange={(e) => setFormData({ ...formData, configSchemaJson: e.target.value })}
                placeholder='{"language": {"type": "select", "label": {"de": "Sprache", "en": "Language"}, ...}}'
                className="min-h-48 font-mono text-xs"
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSaveModel} disabled={saving}>
                {saving ? 'Speichern...' : editingIndex !== null ? 'Speichern' : 'Erstellen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modelle exportieren</DialogTitle>
            <DialogDescription>
              {provider.models.length} Modell{provider.models.length !== 1 ? 'e' : ''} als JSON exportieren
            </DialogDescription>
          </DialogHeader>

          <Textarea
            id="export-json"
            value={exportJson}
            readOnly
            className="min-h-64 font-mono text-xs"
          />

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={handleCopyExport}>
              <Copy className="w-4 h-4 mr-2" />
              Kopieren
            </Button>
            <Button onClick={handleDownloadExport}>
              <Download className="w-4 h-4 mr-2" />
              Herunterladen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modelle importieren</DialogTitle>
            <DialogDescription>
              Importieren Sie Modelle als JSON. Bestehende Modelle mit gleichem Namen werden aktualisiert.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="paste">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="paste">JSON einfügen</TabsTrigger>
              <TabsTrigger value="file">Datei hochladen</TabsTrigger>
            </TabsList>

            <TabsContent value="paste" className="space-y-3">
              <Textarea
                value={importJson}
                onChange={(e) => validateImportJson(e.target.value)}
                placeholder='[{"name": "nova-3", "displayName": {"de": "Nova 3", "en": "Nova 3"}, ...}]'
                className="min-h-48 font-mono text-xs"
              />
            </TabsContent>

            <TabsContent value="file" className="space-y-3">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">JSON-Datei auswählen</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Datei auswählen
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            </TabsContent>
          </Tabs>

          {importError && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              {importError}
            </div>
          )}

          {importPreview && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">
                Vorschau: {importPreview.length} Modell{importPreview.length !== 1 ? 'e' : ''}
              </h4>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {importPreview.map((model, i) => {
                  const exists = provider.models.some(m => m.name === model.name);
                  return (
                    <div key={i} className="flex items-center justify-between p-2 border border-gray-200 rounded">
                      <div>
                        <span className="font-medium text-sm">{getLocalizedText(model.displayName)}</span>
                        <code className="text-xs text-gray-500 ml-2">{model.name}</code>
                      </div>
                      <Badge variant={exists ? 'secondary' : 'default'}>
                        {exists ? 'Aktualisieren' : 'Neu'}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsImportDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleImportConfirm} disabled={!importPreview || saving}>
              {saving ? 'Importieren...' : `${importPreview?.length || 0} Modelle importieren`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
